import type { LocationBootstrapMeter } from '@/features/location-bootstrap/location-bootstrap-types';
import {
  CACHED_METER_STATUSES,
  CACHED_METER_TYPE_LABELS,
  METER_IDENTIFIER_KINDS,
  type MeterIdentifierKind,
  type MeterSearchResult,
  type NormalizedMeterIdentifier,
  normalizeSearchField,
} from '@/shared/meter-identification';

export function findBackendMeterMatches(
  meters: LocationBootstrapMeter[],
  identifier: NormalizedMeterIdentifier,
): MeterSearchResult[] {
  if (!identifier.normalizedValue) {
    return [];
  }

  return meters.flatMap(meter => {
    const matchedBy = getBackendMatchedField(meter, identifier);

    if (!matchedBy) {
      return [];
    }

    return [createMeterSearchResultFromLocationBootstrapMeter(meter, matchedBy)];
  });
}

function getBackendMatchedField(
  meter: LocationBootstrapMeter,
  identifier: NormalizedMeterIdentifier,
): MeterIdentifierKind | null {
  const candidates: Array<{ field: MeterIdentifierKind; value: unknown }> = [
    { field: METER_IDENTIFIER_KINDS.apiKey, value: meter.apiKey },
    { field: METER_IDENTIFIER_KINDS.toolsId, value: meter.toolsId },
    { field: METER_IDENTIFIER_KINDS.devEui, value: meter.devEui },
    { field: METER_IDENTIFIER_KINDS.imsi, value: meter.imsi },
    { field: METER_IDENTIFIER_KINDS.serial, value: meter.serial },
  ];

  return (
    candidates.find(candidate => normalizeUnknownSearchField(candidate.value) === identifier.normalizedValue)?.field ??
    null
  );
}

function createMeterSearchResultFromLocationBootstrapMeter(
  meter: LocationBootstrapMeter,
  matchedBy: MeterIdentifierKind = METER_IDENTIFIER_KINDS.unknown,
): MeterSearchResult {
  return {
    data: {
      _id: meter._id,
      accessibility: meter.accessibility,
      apiKey: meter.apiKey,
      buildingId: meter.buildingId,
      buildingName: meter.buildingName,
      calibrated: meter.calibrated,
      combinedMeterId: meter._id,
      detail: meter.detail,
      devEui: meter.devEui,
      floorName: meter.floorName,
      floorplanLat: meter.floorplanLat,
      floorplanLng: meter.floorplanLng,
      gpsLat: meter.gpsLat,
      gpsLng: meter.gpsLng,
      id: meter.id,
      imsi: meter.imsi,
      internalNote: meter.internalNote,
      internalSupplyPointCode: meter.internalSupplyPointCode,
      locationId: readLocationIdFromMeter(meter),
      lorawanRssi: meter.lorawanRssi,
      manufactured: meter.manufactured,
      manufacturerId: meter.manufacturerId,
      meterModelId: meter.meterModelId,
      commodity: meter.commodity ?? CACHED_METER_TYPE_LABELS.backend,
      mountTechnology: meter.mountTechnology,
      mountType: meter.mountType,
      name: meter.name,
      newMeter: meter.newMeter,
      nbiotRssi: meter.nbiotRssi,
      ownerName: meter.ownerName,
      ownerType: meter.ownerType,
      placement: meter.placement,
      registers: meter.registers,
      remoteReadingOwner: meter.remoteReadingOwner,
      remoteReadingSerial: meter.remoteReadingSerial,
      requiresManualAks: meter.requiresManualAks,
      serial: meter.serial,
      status: meter.status ?? CACHED_METER_STATUSES.downloaded,
      submittedBy: meter.submittedBy,
      tenant: meter.tenant,
      toolsId: meter.toolsId,
      updatedAt: meter.updatedAt,
    },
    id: `backend-meter:${meter._id}`,
    matchedBy,
  };
}

function readLocationIdFromMeter(meter: LocationBootstrapMeter) {
  const raw = meter.raw as { inventory_location?: { id?: number | string } };
  const location = raw.inventory_location?.id;

  return typeof location === 'number' || typeof location === 'string' ? location : undefined;
}

function normalizeUnknownSearchField(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return normalizeSearchField(value.toString());
  }

  return typeof value === 'string' ? normalizeSearchField(value) : undefined;
}
